import { useState } from "react";
import { X, Sparkles, Loader2, ShieldCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * A warm, whimsical paywall inviting the visitor to unlock the whole
 * coloring book for a small one-time fee.
 *
 * Props:
 *  - open        boolean
 *  - onClose     () => void
 *  - book        { id, title }
 *  - lookupKey   Stripe lookup_key for this book
 *  - priceLabel  e.g. "$3.99"
 *  - pageCount   e.g. 9
 *  - reason      what triggered the paywall (for the copy)
 */
export default function ColoringPaywallModal({
  open,
  onClose,
  book,
  lookupKey,
  priceLabel = "$3.99",
  pageCount,
  reason = "shuffle",
  allBooks = [],           // for the bundle upsell
  bundleLookupKey = "coloring-book-bundle",
  bundlePriceLabel = "$9.99",
}) {
  const [busy, setBusy] = useState(false);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [restoreEmail, setRestoreEmail] = useState("");
  const [restoreBusy, setRestoreBusy] = useState(false);

  if (!open) return null;

  const headline = {
    shuffle: "Loving these? Unlock the whole ebook!",
    seeAll: "Ready to see every page?",
    download: "Get the whole ebook — instant download!",
  }[reason] || "Unlock the whole coloring ebook!";

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        book_id: book.id,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start checkout. Try again?");
      setBusy(false);
    }
  };

  const sendMagicLink = async (e) => {
    e.preventDefault();
    if (!restoreEmail) return;
    setRestoreBusy(true);
    try {
      await api.post("/auth/magic-link", { email: restoreEmail });
      toast.success("If that email has an account, a sign-in link is on its way 💌");
      setShowRestore(false);
      setRestoreEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send the link. Try again?");
    } finally {
      setRestoreBusy(false);
    }
  };

  const buyBundle = async () => {
    setBundleBusy(true);
    try {
      const ids = (allBooks || []).map((b) => b.id).filter(Boolean);
      if (ids.length < 2) throw new Error("Bundle unavailable");
      const { data } = await api.post("/payments/checkout", {
        lookup_key: bundleLookupKey,
        book_id: "bundle",
        origin_url: window.location.origin,
        bundle_book_ids: ids,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start bundle checkout.");
      setBundleBusy(false);
    }
  };

  const bundleAvailable = Array.isArray(allBooks) && allBooks.length >= 2;
  const bundleSavings = "Best deal";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(45,48,85,0.6)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="coloring-paywall-modal"
    >
      <div
        className="w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg,#FFF3F8 0%,#FFFFFF 45%,#F0EBFF 100%)",
          boxShadow: "0 40px 80px rgba(45,48,85,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-6 sm:p-8">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors hover:bg-[#FFE1EC]"
            style={{ color: "#7E82A1" }}
            aria-label="Close"
            data-testid="coloring-paywall-close"
          >
            <X size={18} />
          </button>

          <div className="flex items-start gap-3">
            <span
              className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background: "linear-gradient(135deg,#FFD93D 0%,#FF8C42 100%)",
                color: "#FFFFFF",
                boxShadow: "0 10px 22px rgba(255,140,66,0.30)",
              }}
            >
              <Sparkles size={22} strokeWidth={2.2} />
            </span>
            <div>
              <span className="font-caveat text-2xl" style={{ color: "#FF6B9D" }}>
                one-time · no subscription
              </span>
              <h2 className="font-fredoka text-3xl leading-tight" style={{ color: "#2D3055" }}>
                {headline}
              </h2>
            </div>
          </div>

          <ul className="mt-5 space-y-2 font-nunito text-sm" style={{ color: "#3D405B" }}>
            <li className="flex items-start gap-2">
              <span style={{ color: "#FF6B9D" }}>♥</span>
              <span>Instant PDF ebook — all <b>{pageCount}</b> hand-drawn pages from <i>{book?.title}</i></span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: "#FF6B9D" }}>♥</span>
              <span>Download once, keep forever — print at home whenever you like</span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: "#FF6B9D" }}>♥</span>
              <span>Digital delivery only — nothing shipped, no waiting</span>
            </li>
            <li className="flex items-start gap-2">
              <span style={{ color: "#FF6B9D" }}>♥</span>
              <span>We'll set up your little account so your unlock lives with you across devices</span>
            </li>
          </ul>

          <div
            className="mt-6 rounded-2xl p-4 flex items-center justify-between"
            style={{ background: "#FFFFFF", border: "1px solid #F0EBFF" }}
          >
            <div>
              <p className="font-caveat text-lg" style={{ color: "#7E82A1" }}>one-time price</p>
              <p className="font-fredoka text-4xl leading-none" style={{ color: "#2D3055" }}>
                {priceLabel}
              </p>
            </div>
            <button
              type="button"
              onClick={startCheckout}
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm text-white transition-transform hover:-translate-y-0.5 disabled:opacity-70"
              style={{
                background: "linear-gradient(135deg,#FF6B9D 0%,#FF8C42 100%)",
                boxShadow: "0 12px 26px rgba(255,107,157,0.35)",
              }}
              data-testid="coloring-paywall-buy"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending you to checkout…
                </>
              ) : (
                <>
                  <Sparkles size={16} /> Unlock the book
                </>
              )}
            </button>
          </div>

          <div
            className="mt-4 flex items-center justify-center gap-2 text-xs font-nunito"
            style={{ color: "#7E82A1" }}
          >
            <ShieldCheck size={14} /> Secure checkout by Stripe · Cards accepted worldwide
          </div>

          {bundleAvailable && (
            <button
              type="button"
              onClick={buyBundle}
              disabled={bundleBusy}
              className="mt-4 w-full rounded-2xl p-3 text-left transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg,#FFF3F8 0%,#F0EBFF 100%)",
                border: "1.5px dashed #C7B5FF",
              }}
              data-testid="coloring-paywall-bundle"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-caveat text-lg leading-none" style={{ color: "#6B47C7" }}>
                    or grab the whole trio ✨
                  </p>
                  <p className="font-fredoka text-base mt-0.5" style={{ color: "#2D3055" }}>
                    All {allBooks.length} coloring ebooks — {bundlePriceLabel}
                  </p>
                </div>
                <span
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full font-fredoka text-xs"
                  style={{ background: "#6B47C7", color: "#FFFFFF" }}
                >
                  {bundleBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {bundleSavings}
                </span>
              </div>
            </button>
          )}

          <p className="mt-3 text-center text-xs font-nunito" style={{ color: "#7E82A1" }}>
            Already bought it on another device?{" "}
            <button
              type="button"
              onClick={() => setShowRestore((v) => !v)}
              className="underline"
              style={{ color: "#6EC4E8" }}
              data-testid="coloring-paywall-restore-hint"
            >
              Send me a sign-in link
            </button>
            {" "}or sign in with Google using the same email.
          </p>

          {showRestore && (
            <form onSubmit={sendMagicLink} className="mt-3 flex gap-2" data-testid="coloring-paywall-magic-form">
              <input
                type="email"
                required
                value={restoreEmail}
                onChange={(e) => setRestoreEmail(e.target.value)}
                placeholder="you@lovely.com"
                className="flex-1 px-3 py-2 rounded-full text-sm outline-none border font-nunito"
                style={{ borderColor: "#F0D9E8", background: "#FFFFFF", color: "#2D3055" }}
                data-testid="coloring-paywall-magic-email"
              />
              <button
                type="submit"
                disabled={restoreBusy}
                className="px-3 py-2 rounded-full inline-flex items-center gap-1 justify-center font-fredoka text-xs disabled:opacity-60"
                style={{ background: "#6EC4E8", color: "#FFFFFF" }}
                data-testid="coloring-paywall-magic-submit"
              >
                {restoreBusy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                Send link
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
